import { ThreePlPartner } from '../../../three-pl-partners/domain/entities/partner-entity';
import { APIKey } from '../../domain/entities/api-key-entity';
import { Client } from '../../domain/entities/client-entity';
import { ClientRepository } from '../../domain/repositories/client-repository';
import { CreateClientRequest } from '../../presentation/redux/types/client-types';
import { addClientPartner } from '../datasources/add-client-partners';
import { createClient } from '../datasources/create-client';
import { createSuperUser } from '../datasources/create-super-user';
import { deleteClientPartner } from '../datasources/delete-client-partner';
import { disableReceiveAllDriverLocation } from '../datasources/disable-all-driver-location-updates';
import { disableLogo } from '../datasources/disable-logo';
import { disableRatings } from '../datasources/disable-ratings';
import { editClient } from '../datasources/edit-client';
import { editEmailNotifications } from '../datasources/edit-email-notifications';
import { editHubLevelClustering } from '../datasources/edit-hub-level-clustering';
import { editOtpSmsNotifications } from '../datasources/edit-otp-notifications';
import { editPersistDriverLocation } from '../datasources/edit-persist-driver-location';
import { editSmsNotifications } from '../datasources/edit-sms-notifications';
import { editSuperUser } from '../datasources/edit-super-user';
import { editTakealotIntegration } from '../datasources/edit-takealot-integration';
import { enableReceiveAllDriverLocation } from '../datasources/enable-all-driver-location-updates';
import { enableLogo } from '../datasources/enable-logo';
import { enableRatings } from '../datasources/enable-ratings';
import { getApiKey } from '../datasources/get-api-key';
import { getClient } from '../datasources/get-client';
import { getClients } from '../datasources/get-clients';
import { getPartners } from '../datasources/get-partners';
import { getSMSApiKeys } from '../datasources/get-sms-api-keys';
import { grantAppChatBot } from '../datasources/grant-app-chat-bot';
import { grantCustomFieldsPermissions } from '../datasources/grant-customer-custom-fields';
import { grantCustomerDatabase } from '../datasources/grant-customer-database';
import { grantDeliveryMap } from '../datasources/grant-delivery-map';
import { grantFlightboardAccess } from '../datasources/grant-flightboard-access';
import { grantGoogleMapsApi } from '../datasources/grant-google-maps-api';
import { grantQRTypingPermissions } from '../datasources/grant-qr-typing-permissions';
import { grantRoutePlanning } from '../datasources/grant-route-planning';
import { grantSecondaryDriverPermissions } from '../datasources/grant-secondary-driver-permissions';
import { editRouteOptimization } from '../datasources/edit-route-optimization';
import { grantServicePermissions } from '../datasources/grant-service-permissions';
import { grantUseOfThirdPartyApis } from '../datasources/grant-use-of-third-party-apis';
import { grantWalletFeature } from '../datasources/grant-wallet-feature';
import { grantWalletFeatureInitial } from '../datasources/grant-wallet-feature-initial';
import { revokeQRTypingPermissions } from '../datasources/revoke-qr-typing-permissions';
import { revokeSecondaryDriverPermissions } from '../datasources/revoke-secondary-driver-permissions';
import { revokeServicePermissions } from '../datasources/revoke-service-permissions';
import { revokeUseOfThirdPartyApis } from '../datasources/revoke-use-of-third-party-apis';
import { revokeWalletFeature } from '../datasources/revoke-wallet-feature';
import { uploadLogo } from '../datasources/upload-logo';

export class ClientRepositoryImpl implements ClientRepository {
  async CreateClient(createClientRequest: CreateClientRequest): Promise<Client> {
    const result = await createClient(createClientRequest);

    return new Client(result);
  }

  async GetClients(): Promise<Client[]> {
    const clients = await getClients();

    return clients.map((client: any) => new Client(client));
  }

  async GetClient(client_id: string): Promise<Client> {
    const result = await getClient(client_id);

    return new Client(result);
  }

  async GetApiKey(client_id: string): Promise<APIKey> {
    const { key } = await getApiKey(client_id);
    return new APIKey([key]);
  }

  async GetSMSAPIKeys(client_id: string): Promise<APIKey> {
    const { apiKey1, apiKey2 } = await getSMSApiKeys(client_id);

    return new APIKey([apiKey1, apiKey2]);
  }

  async EditClient(client_id: string, name: string): Promise<Client> {
    const result = await editClient(client_id, name);

    return new Client(result);
  }

  async CreateSuperUser(
    client_id: string,
    firstname: string,
    lastname: string,
    email: string,
    mobile_no: string
  ): Promise<Client> {
    const result = await createSuperUser(client_id, firstname, lastname, email, mobile_no);

    return new Client(result);
  }

  async EditSuperUser(
    client_id: string,
    user_id: string,
    firstname: string,
    lastname: string
  ): Promise<Client> {
    const result = await editSuperUser(client_id, user_id, firstname, lastname);

    return new Client(result);
  }

  async UploadLogo(client_id: string, file: any): Promise<Client> {
    await uploadLogo(client_id, file);
    const result = await getClient(client_id);

    return new Client(result);
  }

  async GrantServicePermissions(client_id: string): Promise<Client> {
    await grantServicePermissions(client_id);
    const result = await getClient(client_id);

    return new Client(result);
  }
  async RevokeServicePermissions(client_id: string): Promise<Client> {
    await revokeServicePermissions(client_id);
    const result = await getClient(client_id);

    return new Client(result);
  }

  async GrantQRTypingPermissions(client_id: string): Promise<Client> {
    await grantQRTypingPermissions(client_id);
    const result = await getClient(client_id);

    return new Client(result);
  }
  async RevokeQRTypingPermissions(client_id: string): Promise<Client> {
    await revokeQRTypingPermissions(client_id);
    const result = await getClient(client_id);

    return new Client(result);
  }

  async GrantSecondaryDriverPermissions(client_id: string): Promise<Client> {
    await grantSecondaryDriverPermissions(client_id);
    const result = await getClient(client_id);

    return new Client(result);
  }
  async RevokeSecondaryDriverPermissions(client_id: string): Promise<Client> {
    await revokeSecondaryDriverPermissions(client_id);
    const result = await getClient(client_id);

    return new Client(result);
  }

  async GrantWalletFeature(client_id: string): Promise<Client> {
    await grantWalletFeature(client_id);
    const result = await getClient(client_id);

    return new Client(result);
  }

  async GrantWalletFeatureInitial(
    client_id: string,
    public_key: string,
    private_key: string
  ): Promise<Client> {
    await grantWalletFeatureInitial(client_id, public_key, private_key);
    const result = await getClient(client_id);

    return new Client(result);
  }

  async RevokeWalletFeature(client_id: string): Promise<Client> {
    await revokeWalletFeature(client_id);
    const result = await getClient(client_id);

    return new Client(result);
  }

  async GrantUseOfThirdPartyApis(client_id: string): Promise<Client> {
    await grantUseOfThirdPartyApis(client_id);
    const result = await getClient(client_id);

    return new Client(result);
  }
  async RevokeUseOfThirdPartyApis(client_id: string): Promise<Client> {
    await revokeUseOfThirdPartyApis(client_id);
    const result = await getClient(client_id);

    return new Client(result);
  }

  async EnableLogo(client_id: string): Promise<Client> {
    await enableLogo(client_id);
    const result = await getClient(client_id);

    return new Client(result);
  }
  async DisableLogo(client_id: string): Promise<Client> {
    await disableLogo(client_id);
    const result = await getClient(client_id);

    return new Client(result);
  }

  async EnableRatings(client_id: string): Promise<Client> {
    await enableRatings(client_id);
    const result = await getClient(client_id);

    return new Client(result);
  }
  async DisableRatings(client_id: string): Promise<Client> {
    await disableRatings(client_id);
    const result = await getClient(client_id);

    return new Client(result);
  }

  async EnableReceiveAllDriverLocation(client_id: string): Promise<Client> {
    await enableReceiveAllDriverLocation(client_id);
    const result = await getClient(client_id);

    return new Client(result);
  }
  async DisableReceiveAllDriverLocation(client_id: string): Promise<Client> {
    await disableReceiveAllDriverLocation(client_id);
    const result = await getClient(client_id);

    return new Client(result);
  }

  async GetPartners(): Promise<ThreePlPartner[]> {
    const partners = await getPartners();
    return partners.map((partner: any) => new ThreePlPartner(partner));
  }
  async AddClientPartner(client_id: string, partners: []): Promise<Client> {
    const result = await addClientPartner(client_id, partners);
    return new Client(result);
  }
  async DeleteClientPartner(client_id: string, partner: object): Promise<Client> {
    const result = await deleteClientPartner(client_id, partner);
    return new Client(result);
  }
  async GrantClientCustomPaymentFields(
    client_id: string,
    allow_custom_fields: boolean
  ): Promise<Client> {
    const result = await grantCustomFieldsPermissions(client_id, allow_custom_fields);
    return new Client(result);
  }
  async GrantGoogleMapsApi(client_id: string, google_maps_api: boolean): Promise<Client> {
    const result = await grantGoogleMapsApi(client_id, google_maps_api);
    return new Client(result);
  }
  async GrantSmsNotifications(client_id: string): Promise<Client> {
    await editSmsNotifications({ client_id, enabled: true });
    const result = await getClient(client_id);

    return new Client(result);
  }
  async RevokeSmsNotifications(client_id: string): Promise<Client> {
    await editSmsNotifications({ client_id, enabled: false });
    const result = await getClient(client_id);

    return new Client(result);
  }

  async GrantOtpSmsNotifications(client_id: string): Promise<Client> {
    try {
      await editOtpSmsNotifications({ client_id, enabled: true });
      const result = await getClient(client_id);
      return new Client(result);
    } catch (error) {
      console.error('Error granting OTP SMS notifications:', error);
      throw error;
    }
  }

  async RevokeOtpSmsNotifications(client_id: string): Promise<Client> {
    try {
      await editOtpSmsNotifications({ client_id, enabled: false });
      const result = await getClient(client_id);
      return new Client(result);
    } catch (error) {
      console.error('Error revoking OTP SMS notifications:', error);
      throw error;
    }
  }

  async GrantEmailNotifications(client_id: string): Promise<Client> {
    await editEmailNotifications(client_id, true);
    const result = await getClient(client_id);

    return new Client(result);
  }
  async RevokeEmailNotifications(client_id: string): Promise<Client> {
    await editEmailNotifications(client_id, false);
    const result = await getClient(client_id);

    return new Client(result);
  }
  async GrantRoutePlanning(client_id: string, route_planning_enabled: boolean): Promise<Client> {
    const result = await grantRoutePlanning(client_id, route_planning_enabled);
    return new Client(result);
  }

  async GrantCustomerDatabase(
    client_id: string,
    customer_details_database: boolean
  ): Promise<Client> {
    const result = await grantCustomerDatabase(client_id, customer_details_database);
    return new Client(result);
  }

  async GrantDeliveryMap(client_id: string, delivery_map_enabled: boolean): Promise<Client> {
    const result = await grantDeliveryMap(client_id, delivery_map_enabled);
    return new Client(result);
  }

  async GrantAppChatBot(client_id: string, enabled: boolean): Promise<Client> {
    const result = await grantAppChatBot(client_id, enabled);
    return new Client(result);
  }

  async UpdateSmsProvider(
    client_id: string,
    sms_provider: string,
    api_key1: string,
    api_key2: string
  ): Promise<Client> {
    const result = await editSmsNotifications({
      client_id,
      enabled: true,
      smsProvider: sms_provider,
      apiKey1: api_key1,
      apiKey2: api_key2
    });
    return new Client(result);
  }

  async EditTakealotIntegration(client_id: string, enabled: boolean): Promise<Client> {
    const result = await editTakealotIntegration(client_id, enabled);
    return new Client(result);
  }

  async EditPersistDriverLocation(client_id: string, enabled: boolean): Promise<Client> {
    const result = await editPersistDriverLocation(client_id, enabled);
    return new Client(result);
  }

  async GrantFlightboardAccess(client_id: string, enabled: boolean): Promise<Client> {
    const result = await grantFlightboardAccess(client_id, enabled);
    return new Client(result);
  }

  async EnableHubLevelClustering(client_id: string): Promise<Client> {
    const result = await editHubLevelClustering(client_id, true);
    return new Client(result);
  }

  async DisableHubLevelClustering(client_id: string): Promise<Client> {
    const result = await editHubLevelClustering(client_id, false);
    return new Client(result);
  }

  async EditRouteOptimization(client_id: string, return_geometry: boolean): Promise<Client> {
    const result = await editRouteOptimization(client_id, return_geometry);
    return new Client(result);
  }
}
